/* RESPONSIBLE TEAM: team-inbox */
import type RenderablePart from './renderable-part';
import { RenderableType } from 'embercom/models/data/inbox/renderable-types';
import type UserComment from './renderable/user-comment';
import type AdminComment from './renderable/admin-comment';
import type QuickReplyResponse from './renderable/quick-reply-response';
import type UserEmailComment from './renderable/user-email-comment';
import type Chat from './renderable/chat';
import type Email from './renderable/email';
import type Post from './renderable/post';
import type Tag from './tag';
import type CustomBot from './renderable/custom-bot';
import type BotComment from './renderable/bot-comment';
import type MergedUserComment from './renderable/merged-user-comment';
import type FinAnswer from './renderable/fin-answer';

export default interface TaggablePart extends RenderablePart {
  renderableType:
    | RenderableType.AdminComment
    | RenderableType.BotComment
    | RenderableType.Chat
    | RenderableType.CustomBot
    | RenderableType.Email
    | RenderableType.FinAnswer
    | RenderableType.MergedUserComment
    | RenderableType.Post
    | RenderableType.QuickReplyResponse
    | RenderableType.UserComment
    | RenderableType.UserEmailComment;

  renderableData:
    | AdminComment
    | BotComment
    | Chat
    | CustomBot
    | Email
    | FinAnswer
    | MergedUserComment
    | Post
    | QuickReplyResponse
    | UserComment
    | UserEmailComment;
}

export function isTaggable(part: RenderablePart): part is TaggablePart {
  return [
    RenderableType.AdminComment,
    RenderableType.BotComment,
    RenderableType.Chat,
    RenderableType.CustomBot,
    RenderableType.Email,
    RenderableType.FinAnswer,
    RenderableType.MergedUserComment,
    RenderableType.Post,
    RenderableType.QuickReplyResponse,
    RenderableType.UserComment,
    RenderableType.UserEmailComment,
  ].includes(part.renderableType);
}

export function isPartTaggedWith(part: TaggablePart, tag: Tag): boolean {
  let { tags }: { tags: Array<Tag> } = part.renderableData;
  return tags.some((t) => isSameTag(tag, t));
}

export function includesTag(tags: Tag[], tag: Tag): boolean {
  return tags.any((t) => isSameTag(tag, t));
}

export function isSameTag(a: Tag, b: Tag): boolean {
  return a.id === b.id || a.name === b.name;
}

export function assertTaggablePart(part: RenderablePart): asserts part is TaggablePart {
  if (!isTaggable(part)) {
    throw new Error('Part is not taggable');
  }
}
